<template>
  <div
    class="toast-item"
    :class="[`toast-item--${variant}`, { 'toast-item--dark': dark }]"
    :style="toastStyle"
    @mouseenter="pauseCountdown"
    @mouseleave="resumeCountdown"
  >
    <button
      type="button"
      class="toast-item__close"
      :aria-label="$t('common.actions.close')"
      @click="$emit('close')"
    >
      <PhX
        :size="18"
        weight="bold"
      />
    </button>

    <div
      class="toast-item__icon"
      :class="`toast-item__icon--${variant}`"
    >
      <PhCheckCircle
        v-if="variant === 'success'"
        :size="20"
        weight="fill"
      />
      <PhXCircle
        v-else-if="variant === 'error'"
        :size="20"
        weight="fill"
      />
      <PhWarning
        v-else-if="variant === 'warning'"
        :size="20"
        weight="fill"
      />
      <PhInfo
        v-else
        :size="20"
        weight="fill"
      />
    </div>

    <div class="toast-item__content">
      <div class="toast-item__title">{{ title }}</div>
      <div
        v-if="message"
        class="toast-item__message"
      >
        {{ message }}
      </div>
      <div
        v-if="hasActions"
        class="toast-item__actions"
      >
        <a
          v-for="(action, index) in actions"
          :key="index"
          href="#"
          class="toast-item__action-link"
          @click.prevent="onAction(action)"
        >
          {{ action.label }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import { PhCheckCircle, PhX, PhXCircle, PhWarning, PhInfo } from 'phosphor-vue';

export default {
  name: 'ToastItem',
  components: {
    PhCheckCircle,
    PhX,
    PhXCircle,
    PhWarning,
    PhInfo
  },
  props: {
    variant: {
      type: String,
      default: 'information',
      validator: (v) => ['success', 'error', 'warning', 'information'].includes(v)
    },
    title: {
      type: String,
      required: true
    },
    message: {
      type: String,
      default: ''
    },
    actions: {
      type: Array,
      default: () => []
      // [{ label: 'Undo', handler: fn }, { label: 'View profile', href: '...' }]
    },
    dark: {
      type: Boolean,
      default: false
    },
    duration: {
      type: Number,
      default: 5000
    }
  },
  computed: {
    hasActions() {
      return !!(this.actions?.length);
    },
    toastStyle() {
      const colors = {
        success: this.dark
          ? 'rgba(30, 83, 38, 0.60)'
          : 'rgba(232, 245, 233, 0.60)',
        error: this.dark
          ? 'rgba(127, 29, 29, 0.60)'
          : 'rgba(255, 235, 238, 0.60)',
        warning: this.dark
          ? 'rgba(120, 53, 15, 0.60)'
          : 'rgba(255, 249, 196, 0.60)',
        information: this.dark
          ? 'rgba(26, 35, 126, 0.60)'
          : 'rgba(227, 242, 253, 0.60)'
      };

      return {
        background: colors[this.variant] || colors.information,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)'
      };
    }
  },
  data() {
    return {
      progress: 100,
      isPaused: false,
      timerId: null,
      progressIntervalId: null,
      startTime: null,
      remainingMs: 0
    };
  },
  mounted() {
    if (this.duration > 0) this.startCountdown();
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    startCountdown() {
      this.clearTimers();

      if (this.remainingMs <= 0) this.remainingMs = this.duration;

      this.startTime = Date.now();
      this.progress = 100 * (this.remainingMs / this.duration);

      const updateProgress = () => {
        if (this.isPaused) return;

        const elapsed = Date.now() - this.startTime;
        this.progress = Math.max(0, 100 * ((this.remainingMs - elapsed) / this.duration));

        if (this.progress <= 0) {
          this.clearTimers();
          this.$emit('close');
        }
      };

      this.progressIntervalId = setInterval(updateProgress, 50);
      this.timerId = setTimeout(() => {
        this.clearTimers();
        this.$emit('close');
      }, this.remainingMs);
    },
    pauseCountdown() {
      if (this.duration <= 0) return;

      this.isPaused = true;
      clearTimeout(this.timerId);
      clearInterval(this.progressIntervalId);

      const elapsed = Date.now() - this.startTime;
      this.remainingMs = Math.max(0, this.remainingMs - elapsed);
    },
    resumeCountdown() {
      if (this.duration <= 0 || this.remainingMs <= 0) return;

      this.isPaused = false;
      this.startCountdown();
    },
    clearTimers() {
      if (this.timerId) clearTimeout(this.timerId);

      if (this.progressIntervalId) clearInterval(this.progressIntervalId);

      this.timerId = null;
      this.progressIntervalId = null;
    },
    onAction(action) {
      if (typeof action.handler === 'function') action.handler();

      this.$emit('action', action);
    }
  }
};
</script>

<style lang="scss" scoped>
.toast-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 1rem 2.5rem 1rem 1rem;
  min-width: 320px;
  max-width: 420px;
  border-radius: var(--tc-radius-sm);
  font-family: var(--tc-font-body);
  overflow: hidden;
  border: 1px solid;
}

.toast-item--success {
  border-color: var(--tc-success);
}

.toast-item--information {
  border-color: var(--tc-action);
}

.toast-item--warning {
  border-color: var(--tc-warning);
}

.toast-item--error {
  border-color: var(--tc-danger);
}

.toast-item__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: var(--tc-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s, background 0.15s;
  opacity: 0.7;
}

.toast-item__close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.08);
}

.toast-item--success .toast-item__close {
  color: var(--v-notificationSuccessBorder-base);
}

.toast-item--information .toast-item__close {
  color: var(--v-notificationInformationBorder-base);
}

.toast-item--warning .toast-item__close {
  color: var(--v-notificationWarningBorder-base);
}

.toast-item--error .toast-item__close {
  color: var(--v-notificationErrorBorder-base);
}

.toast-item--dark.toast-item--success .toast-item__close {
  color: var(--v-notificationSuccessText-base);
}

.toast-item--dark.toast-item--information .toast-item__close {
  color: var(--v-notificationInformationText-base);
}

.toast-item--dark.toast-item--warning .toast-item__close {
  color: var(--v-notificationWarningText-base);
}

.toast-item--dark.toast-item--error .toast-item__close {
  color: var(--v-notificationErrorText-base);
}

.toast-item__icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.toast-item__icon--success {
  color: var(--v-notificationSuccessBorder-base);
}

.toast-item__icon--error {
  color: var(--v-notificationErrorBorder-base);
}

.toast-item__icon--warning {
  color: var(--v-notificationWarningBorder-base);
}

.toast-item__icon--information {
  color: var(--v-notificationInformationBorder-base);
}

.toast-item--dark .toast-item__icon--success,
.toast-item--dark.toast-item--success .toast-item__icon--success {
  color: var(--v-notificationSuccessText-base);
}

.toast-item--dark .toast-item__icon--error,
.toast-item--dark.toast-item--error .toast-item__icon--error {
  color: var(--v-notificationErrorText-base);
}

.toast-item--dark .toast-item__icon--warning,
.toast-item--dark.toast-item--warning .toast-item__icon--warning {
  color: var(--v-notificationWarningText-base);
}

.toast-item--dark .toast-item__icon--information,
.toast-item--dark.toast-item--information .toast-item__icon--information {
  color: var(--v-notificationInformationText-base);
}

.toast-item__content {
  flex: 1;
  min-width: 0;
}

.toast-item__title {
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.35;
  margin-bottom: 0.25rem;
}

.toast-item--success .toast-item__title {
  color: var(--v-notificationSuccessText-base);
}

.toast-item--error .toast-item__title {
  color: var(--v-notificationErrorText-base);
}

.toast-item--warning .toast-item__title {
  color: var(--v-notificationWarningText-base);
}

.toast-item--information .toast-item__title {
  color: var(--v-notificationInformationText-base);
}

.toast-item--dark.toast-item--success .toast-item__title {
  color: var(--v-notificationSuccessText-base);
}

.toast-item--dark.toast-item--information .toast-item__title {
  color: var(--v-notificationInformationText-base);
}

.toast-item--dark.toast-item--warning .toast-item__title {
  color: var(--v-notificationWarningText-base);
}

.toast-item--dark.toast-item--error .toast-item__title {
  color: var(--v-notificationErrorText-base);
}

.toast-item__message {
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--tc-text-1);
}

.toast-item--dark .toast-item__message {
  color: var(--tc-text-2);
}

.toast-item__actions {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.toast-item__action-link {
  font-size: 0.875rem;
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.toast-item__action-link:hover {
  opacity: 0.9;
}
</style>
